import { ApiLinkEntity, ApiLinkRel } from "config/constants"
import i18n from "intl/i18n"
import { Guid, IApiLink } from "@/types"
import Address, { IAddressDto } from "./Address"
import ImageCollection, { IGetImageCollectionDto } from "./ImageCollection"
import Location, { ILocationDto } from "./Location"

export enum LabelStatus {
    active = "active",
    inactive = "inactive",
}

export interface IGetLabelGroupDto {
    id: string
    status: LabelStatus
    categoryId?: Guid | null
    articleNumber?: string | null
    serialNumber?: string | null
    serialNumberMachine?: string | null
    inventoryNumber?: string | null
    drawingNumber?: string | null
    manufacturerCode?: string | null
    constructionYear?: number | null
    colorId?: Guid | null
    comment?: string | null
    location: ILocationDto
    address?: IAddressDto | null
    images?: IGetImageCollectionDto
    labels: string[]
    links: IApiLink[]
}

export interface IUpdateLabelGroupDto {
    status?: LabelStatus
    categoryId?: Guid
    articleNumber?: string | null
    serialNumber?: string | null
    serialNumberMachine?: string | null
    inventoryNumber?: string | null
    drawingNumber?: string | null
    manufacturerCode?: string | null
    constructionYear?: number | null
    colorId?: string | null
    comment?: string | null
}

export interface IUpdateLabelGroupLocationDto {
    address?: IAddressDto
}

export default class LabelGroup {
    id: string
    status: LabelStatus
    categoryId?: Guid
    articleNumber?: string
    serialNumber?: string
    serialNumberMachine?: string
    inventoryNumber?: string
    drawingNumber?: string
    manufacturerCode?: string
    constructionYear?: number
    colorId?: Guid
    comment?: string
    location: Location
    address?: Address
    images?: ImageCollection
    labels: string[]
    linkUpdateLabelGroup?: IApiLink
    linkUpdateLabelGroupLocation?: IApiLink
    linkLinkLabelGroupToAsset?: IApiLink
    linkUnlinkLabelFromLabelGroup?: IApiLink
    linkDeleteLabelFromLabelGroup?: IApiLink
    linkDeleteLabelGroup?: IApiLink

    constructor(dto: IGetLabelGroupDto) {
        this.id = dto.id
        this.status = dto.status
        this.categoryId = dto.categoryId ?? undefined
        this.articleNumber = dto.articleNumber ?? undefined
        this.serialNumber = dto.serialNumber ?? undefined
        this.serialNumberMachine = dto.serialNumberMachine ?? undefined
        this.inventoryNumber = dto.inventoryNumber ?? undefined
        this.drawingNumber = dto.drawingNumber ?? undefined
        this.manufacturerCode = dto.manufacturerCode ?? undefined
        this.constructionYear = typeof dto.constructionYear === "number" ? dto.constructionYear : undefined
        this.colorId = dto.colorId ?? undefined
        this.comment = dto.comment ?? undefined
        this.location = new Location(dto.location)
        this.address = dto.address ? new Address(dto.address) : undefined
        this.images = dto.images ? new ImageCollection(dto.images) : undefined
        this.labels = dto.labels
        this.linkUpdateLabelGroup = dto.links.find(lnk => lnk.entity === ApiLinkEntity.labelGroup && lnk.rel === ApiLinkRel.partialUpdate)
        this.linkUpdateLabelGroupLocation = dto.links.find(lnk => lnk.entity === ApiLinkEntity.labelGroupLocation && lnk.rel === ApiLinkRel.partialUpdate)
        this.linkLinkLabelGroupToAsset = dto.links.find(lnk => lnk.entity === ApiLinkEntity.labelGroupLink && lnk.rel === ApiLinkRel.new)
        this.linkUnlinkLabelFromLabelGroup = dto.links.find(lnk => lnk.entity === ApiLinkEntity.labelGroupLink && lnk.rel === ApiLinkRel.delete)
        this.linkDeleteLabelFromLabelGroup = dto.links.find(lnk => lnk.entity === ApiLinkEntity.label && lnk.rel === ApiLinkRel.delete)
        this.linkDeleteLabelGroup = dto.links.find(lnk => lnk.entity === ApiLinkEntity.labelGroup && lnk.rel === ApiLinkRel.delete)
    }
}

export function getLabelStatusText(status: LabelStatus) {
    return {
        [LabelStatus.active]: i18n.t('label.active'),
        [LabelStatus.inactive]: i18n.t('label.inactive'),
    }[status] || ""
}

export const getLabelStatusColor = (status: LabelStatus): string => {
    return {
        [LabelStatus.active]: 'bg-green-400',
        [LabelStatus.inactive]: 'bg-grey-400',
    }[status] || 'bg-grey-400'
}
